export const M1_UNIT_4 = {
  id: "u4-family-pets",
  title: "Unit 4: Family & Pets",
  topic: "Family Members, Pets & Possessives",
  iconType: "heart",
  grammarImage: "/u4-family-pets.jpg",
  grammarText: "Family vocabulary and Possessive 's. Example: 'My father's car' (El coche de mi padre).",
  
  sets: [
    // ===================================================================
    // SET 1: IMMEDIATE FAMILY (A1.0)
    // ===================================================================
    {
      id: "u4-set1",
      title: "Set 1: My Immediate Family",
      context: `*Casa de Luis - Melbourne, Australia*

Luis es fotógrafo. Hoy organiza fotos de su familia.
"This is my father. This is my mother. This is my sister."
Escribe los nombres debajo de cada foto.

*Luis's House - Melbourne, Australia*

Luis is a photographer. Today he organizes photos of his family.
"This is my father. This is my mother. This is my sister."
He writes the names under each photo.`,
      
      questions: [
        {
          type: "match",
          q: "Match each family word to its casual form.",
          pairs: [
            { left: "Father", right: "Dad" },
            { left: "Mother", right: "Mom" },
            { left: "Parents", right: "Mom & Dad" }
          ],
          a: "completed",
          tip: "Father/Mother = formal. Dad/Mom = casual.",
          successTip: "Dad/Mom = casual (daily use). Father/Mother = formal (documents, introductions). Natives say 'Mom/Dad' 99% of the time! 👨‍👩‍👧"
        },
        
        {
          type: "mc",
          q: "Luis: 'I have one brother and one ___.'",
          options: ["sister", "father", "mother"],
          a: "sister",
          tip: "Brother (male) vs Sister (female)."
        },
        
        {
          type: "mc",
          q: "Luis: 'My mother and father are my ___.'",
          options: ["parents", "grandparents", "cousins"],
          a: "parents",
          tip: "Parents = mother + father together.",
          successTip: "'Parents' = both! Super useful word. 'My parents are doctors' = both work as doctors. One word, two people! 👨‍👩‍👧‍👦"
        },
        
        {
          type: "input",
          q: "Luis: 'He is my father. She is my ___.'",
          a: "mother",
          alts: ["mom", "mum"],
          tip: "Father → Mother (female)."
        },
        
        {
          type: "order",
          q: "Traduce: 'Este es mi hermano.'",
          words: ["This", "is", "my", "brother."],
          a: "This is my brother.",
          extra: ["sister", "father"],
          tip: "'This is' = presenting someone."
        },
        
        {
          type: "match",
          q: "Match male to female family members.",
          pairs: [
            { left: "Brother", right: "Sister" },
            { left: "Husband", right: "Wife" },
            { left: "Son", right: "Daughter" }
          ],
          a: "completed",
          tip: "Male vs Female pairs.",
          successTip: "Gender pairs! Brother/Sister (siblings), Husband/Wife (married), Son/Daughter (children). Pattern! 👨👩"
        },
        
        {
          type: "input",
          q: "Luis's parents have one boy. Luis is their ___.",
          a: "son",
          alts: [],
          tip: "Son = male child."
        },
        
        {
          type: "mc",
          q: "Luis has one girl child. She is his ___.",
          options: ["daughter", "son", "sister"],
          a: "daughter",
          tip: "Daughter = female child.",
          successTip: "Son vs Daughter! Son = your male child. Daughter = your female child. Brother/Sister = your siblings. Different! 👧👦"
        },
        
        {
          type: "order",
          q: "Traduce: 'Amo a mi familia.'",
          words: ["I", "love", "my", "family."],
          a: "I love my family.",
          extra: ["like", "have"],
          tip: "'Love' = amar (strong feeling)."
        },
        
        {
          type: "mc",
          q: "Luis's father is married to Luis's ___.",
          options: ["mother", "sister", "daughter"],
          a: "mother",
          tip: "Father is married to mother.",
          successTip: "Family logic! Father + Mother = married. Father's wife = Mother. Simple! Use logic to learn family! 🧠"
        }
      ]
    },

    // ===================================================================
    // SET 2: EXTENDED FAMILY (A1.0)
    // ===================================================================
    {
      id: "u4-set2",
      title: "Set 2: Visiting Relatives",
      context: `*Casa de los Tíos - Sídney*

Luis visita a su tío y tía en Sídney.
"My uncle is my father's brother. My aunt is his wife."
Sus primos están felices de verlo.

*Uncle's House - Sydney*

Luis visits his uncle and aunt in Sydney.
"My uncle is my father's brother. My aunt is his wife."
His cousins are happy to see him.`,
      
      questions: [
        {
          type: "match",
          q: "Match each relationship.",
          pairs: [
            { left: "My dad's dad", right: "Grandfather" },
            { left: "My mom's sister", right: "Aunt" },
            { left: "My dad's brother", right: "Uncle" }
          ],
          a: "completed",
          tip: "Extended family relationships.",
          successTip: "Family tree logic! Dad's dad = Grandfather. Mom's sister = Aunt. Dad's brother = Uncle. Think step by step! 🌳"
        },
        
        {
          type: "mc",
          q: "Luis: 'My aunt's children are my ___.'",
          options: ["cousins", "brothers", "nephews"],
          a: "cousins",
          tip: "Aunt's children = your cousins.",
          successTip: "Cousins = primos! Your aunt/uncle's kids. Very common word! 'My cousin is 10 years old.' 👫"
        },
        
        {
          type: "mc",
          q: "Luis: 'My grandmother is my mother's ___.'",
          options: ["mother", "sister", "daughter"],
          a: "mother",
          tip: "Grandmother = mother's mother."
        },
        
        {
          type: "input",
          q: "Luis: 'My uncle is married to my ___.'",
          a: "aunt",
          alts: [],
          tip: "Uncle's wife = Aunt."
        },
        
        {
          type: "order",
          q: "Traduce: 'Él es mi tío favorito.'",
          words: ["He", "is", "my", "favorite", "uncle."],
          a: "He is my favorite uncle.",
          extra: ["best", "good"],
          tip: "'Favorite' = favorito."
        },
        
        {
          type: "match",
          q: "Match casual to formal names.",
          pairs: [
            { left: "Grandma", right: "Grandmother" },
            { left: "Grandpa", right: "Grandfather" },
            { left: "Cousin", right: "Aunt's child" }
          ],
          a: "completed",
          tip: "Grandma/Grandpa = casual.",
          successTip: "Grandma/Grandpa = cute/casual! Like saying 'Abuelita/Abuelito'. Grandmother/Grandfather = formal. Use casual with family! 👵👴"
        },
        
        {
          type: "input",
          q: "Luis: 'My brother has one daughter. She is my ___.'",
          a: "niece",
          alts: [],
          tip: "Brother's daughter = your niece.",
          successTip: "Niece (female) vs Nephew (male)! Brother/sister's children = your nieces and nephews. Sobrina/sobrino! 👧👦"
        },
        
        {
          type: "input",
          q: "Luis: 'My sister has one son. He is my ___.'",
          a: "nephew",
          alts: [],
          tip: "Sister's son = your nephew."
        },
        
        {
          type: "order",
          q: "Traduce: 'Visitamos a nuestros abuelos.'",
          words: ["We", "visit", "our", "grandparents."],
          a: "We visit our grandparents.",
          extra: ["my", "the"],
          tip: "'Our' = nuestros/nuestras."
        },
        
        {
          type: "mc",
          q: "Luis: 'My cousin's mother is my ___.'",
          options: ["aunt", "grandmother", "mother"],
          a: "aunt",
          tip: "Cousin's mother = your aunt.",
          successTip: "Family logic puzzle! Cousin's mother = YOUR aunt (because your aunt's child = your cousin). Logic chain! 🧠"
        }
      ]
    },

    // ===================================================================
    // SET 3: PETS & ANIMALS (A1+)
    // ===================================================================
    {
      id: "u4-set3",
      title: "Set 3: Pet Photography",
      context: `*Estudio Fotográfico - Melbourne*

Luis fotografía mascotas hoy.
"I have photos of dogs, cats, and rabbits."
Un cliente pregunta: "Do you have birds?" Luis responde: "Yes!"

*Photo Studio - Melbourne*

Luis photographs pets today.
"I have photos of dogs, cats, and rabbits."
A client asks: "Do you have birds?" Luis answers: "Yes!"`,
      
      questions: [
        {
          type: "match",
          q: "Match each animal to its sound.",
          pairs: [
            { left: "Dog", right: "Woof" },
            { left: "Cat", right: "Meow" },
            { left: "Cow", right: "Moo" }
          ],
          a: "completed",
          tip: "Animals make sounds.",
          successTip: "English animal sounds! Dog = Woof/Bark. Cat = Meow. Cow = Moo. Different from Spanish sounds! 🐕🐱🐄"
        },
        
        {
          type: "mc",
          q: "Client: 'I have a small pet. It is a ___.'",
          options: ["hamster", "horse", "cow"],
          a: "hamster",
          tip: "Hamster = small pet (lives in cage)."
        },
        
        {
          type: "mc",
          q: "Farmer: 'This animal is on my farm. It is a ___.'",
          options: ["cow", "cat", "hamster"],
          a: "cow",
          tip: "Cows live on farms."
        },
        
        {
          type: "input",
          q: "Luis: 'It lives in water. It is a ___.'",
          a: "fish",
          alts: [],
          tip: "Fish live in water."
        },
        
        {
          type: "input",
          q: "Luis: 'It has long ears. It is a ___.'",
          a: "rabbit",
          alts: ["bunny"],
          tip: "Rabbits have long ears.",
          successTip: "'Rabbit' (formal) OR 'Bunny' (cute/kids). Same animal! Kids say 'bunny'. Adults say 'rabbit'. 🐰"
        },
        
        {
          type: "order",
          q: "Traduce: 'Tengo un perro de mascota.'",
          words: ["I", "have", "a", "pet", "dog."],
          a: "I have a pet dog.",
          extra: ["cat", "the"],
          tip: "'Pet' = mascota (animal at home)."
        },
        
        {
          type: "match",
          q: "Match each animal to where it lives.",
          pairs: [
            { left: "Fish", right: "Water" },
            { left: "Bird", right: "Tree" },
            { left: "Horse", right: "Farm" }
          ],
          a: "completed",
          tip: "Animals live in different places.",
          successTip: "Habitats! Fish = water (aquarium/ocean). Birds = trees/sky. Horses = farms/stables. Animals need homes! 🐟🐦🐴"
        },
        
        {
          type: "mc",
          q: "Luis: 'This bird is colorful. It is a ___.'",
          options: ["parrot", "chicken", "duck"],
          a: "parrot",
          tip: "Parrots are colorful birds.",
          successTip: "Parrots talk! Some parrots say 'Hello!' or repeat words. Smart birds! 🦜"
        },
        
        {
          type: "order",
          q: "Traduce: 'El gato está en la casa.'",
          words: ["The", "cat", "is", "in", "the", "house."],
          a: "The cat is in the house.",
          extra: ["on", "at"],
          tip: "'In' = dentro de (inside)."
        },
        
        {
          type: "mc",
          q: "Farmer: 'This animal is big. It is a ___.'",
          options: ["horse", "hamster", "fish"],
          a: "horse",
          tip: "Horses are big animals.",
          successTip: "Size matters! Hamster = small 🐹. Cat/Dog = medium 🐱🐕. Horse/Cow = big 🐴🐄. Use 'big' and 'small'! 📏"
        }
      ]
    },

    // ===================================================================
    // SET 4: POSSESSIVE 'S (A1+)
    // ===================================================================
    {
      id: "u4-set4",
      title: "Set 4: Labeling Photos",
      context: `*Organizando Fotos - Computadora*

Luis organiza fotos en su computadora.
"This is Tom's dog. This is Sarah's cat. This is my dad's car."
Usa el apóstrofe + s para mostrar posesión.

*Organizing Photos - Computer*

Luis organizes photos on his computer.
"This is Tom's dog. This is Sarah's cat. This is my dad's car."
He uses apostrophe + s to show possession.`,
      
      questions: [
        {
          type: "match",
          q: "Match each possessive to its meaning.",
          pairs: [
            { left: "My dad's car", right: "The car of my dad" },
            { left: "Tom's dog", right: "The dog of Tom" },
            { left: "My sister's room", right: "The room of my sister" }
          ],
          a: "completed",
          tip: "'s = possession (belongs to).",
          successTip: "Possessive 's! Dad's car = shorter than 'the car of my dad'. English loves shortcuts! Always use 's! ✅"
        },
        
        {
          type: "mc",
          q: "Luis labels a photo: 'This is ___ ball.'",
          options: ["the dog's", "the dogs", "dog is"],
          a: "the dog's",
          tip: "Dog's = belongs to the dog.",
          successTip: "'The dog's ball' ✅ (possessive). 'The dogs' ❌ (plural). 'Dog is' ❌ (verb). Don't confuse! ⚠️"
        },
        
        {
          type: "mc",
          q: "Luis writes: '___ name is Sarah.'",
          options: ["My mom's", "My moms", "My mom is"],
          a: "My mom's",
          tip: "Mom's = possessive (her name)."
        },
        
        {
          type: "input",
          q: "Luis: 'The phone is my father's. It is ___ phone.'",
          a: "Dad's",
          alts: ["dad's", "my dad's"],
          tip: "Use 's for singular possession."
        },
        
        {
          type: "input",
          q: "Luis: 'The house is my parents'. It is my ___ house.'",
          a: "parents'",
          alts: [],
          tip: "Plural ending in -s: just add apostrophe (').",
          successTip: "Plural possessive! Parents' (plural) = apostrophe AFTER s. Parent's (singular) = apostrophe BEFORE s. Different! 🏠"
        },
        
        {
          type: "order",
          q: "Traduce: 'Este es el hermano de John.'",
          words: ["This", "is", "John's", "brother."],
          a: "This is John's brother.",
          extra: ["brother's", "Johns"],
          tip: "John's = belongs to John."
        },
        
        {
          type: "mc",
          q: "Luis: 'My ___ husband is my father.'",
          options: ["mother's", "mothers", "mother"],
          a: "mother's",
          tip: "Mother's husband = possessive.",
          successTip: "Logic + possessive! Mother's husband = your father. Family + grammar together! 🧠"
        },
        
        {
          type: "input",
          q: "Luis asks a client: 'Is this ___ bag?' (Anna)",
          a: "Anna's",
          alts: ["anna's"],
          tip: "Anna's = belongs to Anna."
        },
        
        {
          type: "order",
          q: "Traduce: 'Ese es el gato de mi hermana.'",
          words: ["That", "is", "my", "sister's", "cat."],
          a: "That is my sister's cat.",
          extra: ["sisters", "cat's"],
          tip: "Sister's = belongs to sister."
        },
        
        {
          type: "mc",
          q: "Luis: 'The ___ toys are here.' (children - irregular plural)",
          options: ["children's", "childrens", "childrens'"],
          a: "children's",
          tip: "'Children' doesn't end in -s, so add 's.",
          successTip: "Irregular plurals! Children → children's. Men → men's. Women → women's. NOT 'childrens' ❌. Add 's because no -s ending! 🧸"
        }
      ]
    },

    // ===================================================================
    // SET 5: THIS/THAT/THESE/THOSE (A1+)
    // ===================================================================
    {
      id: "u4-set5",
      title: "Set 5: Showing Photos",
      context: `*Mostrando Fotos a un Cliente*

Luis muestra fotos a un cliente.
"This is your dog (cerca). That is your cat (lejos)."
"These are your children (cerca). Those are your parents (lejos)."

*Showing Photos to a Client*

Luis shows photos to a client.
"This is your dog (near). That is your cat (far)."
"These are your children (near). Those are your parents (far)."`,
      
      questions: [
        {
          type: "mc",
          q: "Luis points near: '___ is my mother.'",
          options: ["This", "That", "These"],
          a: "This",
          tip: "THIS = singular + near.",
          successTip: "THIS (near) vs THAT (far)! Point at something close = THIS. Point at something far = THAT. Distance matters! 👆👉"
        },
        
        {
          type: "mc",
          q: "Luis points far: '___ is my uncle over there.'",
          options: ["That", "This", "Those"],
          a: "That",
          tip: "THAT = singular + far."
        },
        
        {
          type: "input",
          q: "Luis points near (plural): '___ are my cousins.'",
          a: "These",
          alts: ["these"],
          tip: "THESE = plural + near.",
          successTip: "THESE (plural + near) vs THOSE (plural + far)! More than one person/thing close = THESE. Far = THOSE. 👥"
        },
        
        {
          type: "input",
          q: "Luis points far (plural): '___ are my grandparents.'",
          a: "Those",
          alts: ["those"],
          tip: "THOSE = plural + far."
        },
        
        {
          type: "match",
          q: "Match each word to its use.",
          pairs: [
            { left: "This", right: "One person (here)" },
            { left: "That", right: "One person (there)" },
            { left: "These", right: "Many people (here)" }
          ],
          a: "completed",
          tip: "Singular vs Plural, Near vs Far.",
          successTip: "Easy pattern! THIS/THAT = singular (one). THESE/THOSE = plural (many). Simple! 👆👉👥"
        },
        
        {
          type: "order",
          q: "Traduce: '¿Quién es ese hombre?'",
          words: ["Who", "is", "that", "man?"],
          a: "Who is that man?",
          extra: ["this", "these"],
          tip: "'That' for one person far."
        },
        
        {
          type: "mc",
          q: "Luis: '___ are my sisters in the photo.' (plural + near)",
          options: ["These", "This", "That"],
          a: "These",
          tip: "Sisters = plural, so THESE."
        },
        
        {
          type: "input",
          q: "Luis asks: 'Is ___ your brother?' (singular + near)",
          a: "this",
          alts: ["This"],
          tip: "Singular + near = THIS."
        },
        
        {
          type: "mc",
          q: "Luis: '___ people are my family.' (plural + far)",
          options: ["Those", "These", "That"],
          a: "Those",
          tip: "Plural + far = THOSE.",
          successTip: "'Those people' = many people far away. Perfect use! Plural + far = THOSE always! 👨‍👩‍👧‍👦"
        },
        
        {
          type: "order",
          q: "Traduce: 'Estos son mis padres.'",
          words: ["These", "are", "my", "parents."],
          a: "These are my parents.",
          extra: ["This", "is"],
          tip: "Parents = plural, so THESE."
        }
      ]
    },

    // ===================================================================
    // SET 6: FAMILY DESCRIPTIONS (A2-)
    // ===================================================================
    {
      id: "u4-set6",
      title: "Set 6: Family Album",
      context: `*Creando un Álbum Familiar*

Luis crea un álbum con descripciones.
"My father is 60. My mother is 58. They are my parents."
"I have two brothers. They are 30 and 28."

*Creating a Family Album*

Luis creates an album with descriptions.
"My father is 60. My mother is 58. They are my parents."
"I have two brothers. They are 30 and 28."`,
      
      questions: [
        {
          type: "mc",
          q: "Luis writes: 'I have one brother and one ___.'",
          options: ["sister", "parent", "cousin"],
          a: "sister",
          tip: "Brother + sister = siblings."
        },
        
        {
          type: "input",
          q: "Luis: 'My father is 60. My mother is 58. They are my ___.'",
          a: "parents",
          alts: [],
          tip: "Father + mother = parents."
        },
        
        {
          type: "order",
          q: "Traduce: 'Tengo dos hermanos.'",
          words: ["I", "have", "two", "brothers."],
          a: "I have two brothers.",
          extra: ["brother", "am"],
          tip: "'Have' = tener."
        },
        
        {
          type: "mc",
          q: "Luis: 'My aunt has one son. He is my ___.'",
          options: ["cousin", "brother", "nephew"],
          a: "cousin",
          tip: "Aunt's son = your cousin."
        },
        
        {
          type: "input",
          q: "Luis: 'My brother has one daughter. She is my ___.'",
          a: "niece",
          alts: [],
          tip: "Brother's daughter = your niece."
        },
        
        {
          type: "match",
          q: "Match each family member to their age.",
          pairs: [
            { left: "Father", right: "60 years old" },
            { left: "Mother", right: "58 years old" },
            { left: "Brother", right: "30 years old" }
          ],
          a: "completed",
          tip: "Combining family + ages."
        },
        
        {
          type: "mc",
          q: "Luis: 'I have a pet. It is a ___.'",
          options: ["dog", "table", "house"],
          a: "dog",
          tip: "Pets are animals."
        },
        
        {
          type: "input",
          q: "Luis: 'My uncle is my father's ___.'",
          a: "brother",
          alts: [],
          tip: "Uncle = father's brother.",
          successTip: "Family logic! Uncle = father's brother OR mother's brother. Aunt = their wife. 🌳"
        },
        
        {
          type: "order",
          q: "Traduce: 'Mi familia es grande.'",
          words: ["My", "family", "is", "big."],
          a: "My family is big.",
          extra: ["small", "very"],
          tip: "'Big family' = many members."
        },
        
        {
          type: "mc",
          q: "Luis: 'My grandmother and grandfather are my ___.'",
          options: ["grandparents", "parents", "uncles"],
          a: "grandparents",
          tip: "Grandmother + grandfather = grandparents.",
          successTip: "'Grandparents' = both grandma and grandpa! One word for both. Same pattern: parents, grandparents. 👴👵"
        }
      ]
    },

    // ===================================================================
    // SET 7: FARM ANIMALS (A2-)
    // ===================================================================
    {
      id: "u4-set7",
      title: "Set 7: Farm Photo Shoot",
      context: `*Sesión de Fotos en una Granja*

Luis trabaja en una granja hoy.
"This farm has three cows, two horses, and five chickens."
Luis fotografía cada animal.

*Photo Shoot on a Farm*

Luis works on a farm today.
"This farm has three cows, two horses, and five chickens."
Luis photographs each animal.`,
      
      questions: [
        {
          type: "mc",
          q: "Luis: 'This animal is big. It is a ___.'",
          options: ["cow", "hamster", "fish"],
          a: "cow",
          tip: "Cows are big farm animals."
        },
        
        {
          type: "input",
          q: "Luis: 'This animal is fast. It is a ___.'",
          a: "horse",
          alts: [],
          tip: "Horses are fast."
        },
        
        {
          type: "mc",
          q: "Farmer: 'These animals give eggs. They are ___.'",
          options: ["chickens", "cows", "horses"],
          a: "chickens",
          tip: "Chickens give eggs.",
          successTip: "Farm facts! Chickens → eggs 🥚. Cows → milk 🥛. Horses → transportation 🐴. Each animal has a job! 🚜"
        },
        
        {
          type: "match",
          q: "Match each animal to its number on the farm.",
          pairs: [
            { left: "Cows", right: "Three" },
            { left: "Horses", right: "Two" },
            { left: "Chickens", right: "Five" }
          ],
          a: "completed",
          tip: "Counting animals."
        },
        
        {
          type: "order",
          q: "Traduce: 'La granja tiene tres vacas.'",
          words: ["The", "farm", "has", "three", "cows."],
          a: "The farm has three cows.",
          extra: ["cow", "is"],
          tip: "'Has' = tiene (for farm/place)."
        },
        
        {
          type: "mc",
          q: "Luis: 'This animal is white and black. It is a ___.'",
          options: ["cow", "chicken", "rabbit"],
          a: "cow",
          tip: "Cows are often white and black."
        },
        
        {
          type: "input",
          q: "Luis: 'This small animal is red. It is a ___.'",
          a: "chicken",
          alts: [],
          tip: "Some chickens are red/brown."
        },
        
        {
          type: "mc",
          q: "Farmer: 'This animal is brown. It is a ___.'",
          options: ["horse", "fish", "hamster"],
          a: "horse",
          tip: "Horses are often brown."
        },
        
        {
          type: "input",
          q: "Luis: 'I have ___ (number) cows in this photo.'",
          a: "three",
          alts: ["3"],
          tip: "The farm has three cows."
        },
        
        {
          type: "order",
          q: "Traduce: 'Los caballos son muy rápidos.'",
          words: ["The", "horses", "are", "very", "fast."],
          a: "The horses are very fast.",
          extra: ["slow", "big"],
          tip: "'Very fast' = muy rápidos.",
          successTip: "'Very' = muy! Very big, very small, very fast. Adds intensity! Use before adjectives. 💨"
        }
      ]
    },

    // ===================================================================
    // SET 8: FAMILY QUESTIONS (A2-)
    // ===================================================================
    {
      id: "u4-set8",
      title: "Set 8: Client Questions",
      context: `*Entrevista con Cliente*

Luis pregunta a un cliente sobre su familia.
"Do you have brothers? How many sisters? What is your father's name?"
El cliente responde cada pregunta.

*Client Interview*

Luis asks a client about their family.
"Do you have brothers? How many sisters? What is your father's name?"
The client answers each question.`,
      
      questions: [
        {
          type: "order",
          q: "Traduce: '¿Tienes hermanos?'",
          words: ["Do", "you", "have", "brothers?"],
          a: "Do you have brothers?",
          extra: ["are", "is"],
          tip: "'Do you have...?' = question form.",
          successTip: "'Do you have...?' = asking about possession! Do you have brothers/pets/cars? Standard question! ❓"
        },
        
        {
          type: "mc",
          q: "Client answers: '___'",
          options: ["Yes, I have two brothers", "Yes, I am two brothers", "Yes, I has two brothers"],
          a: "Yes, I have two brothers",
          tip: "'I have' (not 'I am' or 'I has')."
        },
        
        {
          type: "mc",
          q: "Luis: 'Who is that?' Client: '___ is my sister.'",
          options: ["That", "Those", "These"],
          a: "That",
          tip: "THAT = one person far."
        },
        
        {
          type: "input",
          q: "Luis: 'Is your sister married?' Client: 'No, she is ___.'",
          a: "single",
          alts: ["not married"],
          tip: "Single = not married.",
          successTip: "Marital status! Married = casado/a. Single = soltero/a. Useful vocab for forms! 💍"
        },
        
        {
          type: "match",
          q: "Match each question to its answer.",
          pairs: [
            { left: "Do you have pets?", right: "Yes, a cat" },
            { left: "Who is he?", right: "My cousin" },
            { left: "How old is she?", right: "She is 10" }
          ],
          a: "completed",
          tip: "Questions + answers."
        },
        
        {
          type: "order",
          q: "Traduce: 'Tengo una familia grande.'",
          words: ["I", "have", "a", "big", "family."],
          a: "I have a big family.",
          extra: ["small", "is"],
          tip: "'Big family' = many members."
        },
        
        {
          type: "mc",
          q: "Luis: 'Is this your dog?' Client: 'Yes, ___ is.'",
          options: ["it", "he", "she"],
          a: "it",
          tip: "Animals = 'it' (unless you know gender).",
          successTip: "Animals = 'it'! Unless you know: male dog = 'he', female cat = 'she'. But safe answer = 'it'. 🐕"
        },
        
        {
          type: "mc",
          q: "Luis: 'How many children do you have?' Client: '___'",
          options: ["I have three", "I am three", "I has three"],
          a: "I have three",
          tip: "'I have' for possession."
        },
        
        {
          type: "input",
          q: "Luis: 'What is your mother's name?' Client: '___ name is Maria.'",
          a: "Her",
          alts: ["her"],
          tip: "HER = possessive for 'she'.",
          successTip: "Possessive adjectives! My, your, his, HER, our, their. 'Her name' = su nombre (de ella). 👩"
        },
        
        {
          type: "mc",
          q: "Luis: 'Are they your parents?' Client: 'Yes, ___ are.'",
          options: ["they", "we", "you"],
          a: "they",
          tip: "Short answer: 'they are'."
        }
      ]
    },

    // ===================================================================
    // SET 9: POSSESSIVE REVIEW (A2-)
    // ===================================================================
    {
      id: "u4-set9",
      title: "Set 9: Album Labels",
      context: `*Etiquetando el Álbum Final*

Luis etiqueta cada foto del álbum.
"This is my mother's garden. This is my brother's car."
"These are my parents' friends. This is my sister's cat."

*Labeling the Final Album*

Luis labels each photo in the album.
"This is my mother's garden. This is my brother's car."
"These are my parents' friends. This is my sister's cat."`,
      
      questions: [
        {
          type: "mc",
          q: "Luis labels: 'This is ___ car.' (John)",
          options: ["John's", "Johns", "John"],
          a: "John's",
          tip: "John's = belongs to John."
        },
        
        {
          type: "input",
          q: "Luis: 'The cat is my sister's. It is my ___ cat.'",
          a: "sister's",
          alts: [],
          tip: "Sister's = singular possessive."
        },
        
        {
          type: "mc",
          q: "Luis: 'My aunt has one son. He is my ___.'",
          options: ["cousin", "brother", "nephew"],
          a: "cousin",
          tip: "Aunt's son = your cousin."
        },
        
        {
          type: "order",
          q: "Traduce: 'Ese es el perro de mi padre.'",
          words: ["That", "is", "my", "father's", "dog."],
          a: "That is my father's dog.",
          extra: ["fathers", "dog's"],
          tip: "Father's = possessive."
        },
        
        {
          type: "mc",
          q: "Luis points near: '___ is my dog.'",
          options: ["This", "That", "These"],
          a: "This",
          tip: "THIS = near + singular."
        },
        
        {
          type: "mc",
          q: "Luis points far: '___ is my cat over there.'",
          options: ["That", "This", "Those"],
          a: "That",
          tip: "THAT = far + singular."
        },
        
        {
          type: "input",
          q: "Luis: 'The house is my parents'. It is my ___ house.'",
          a: "parents'",
          alts: [],
          tip: "Plural possessive: parents' (apostrophe after s).",
          successTip: "Plural possessive! Parents' house = apostrophe AFTER s (because 'parents' ends in s). ✍️"
        },
        
        {
          type: "match",
          q: "Match each possessive to its type.",
          pairs: [
            { left: "Dog's", right: "Singular" },
            { left: "Dogs'", right: "Plural (ends in s)" },
            { left: "Children's", right: "Plural (no s)" }
          ],
          a: "completed",
          tip: "Different possessive forms.",
          successTip: "Three types! Dog's (singular), Dogs' (plural with s), Children's (irregular plural). Master all three! 📚"
        },
        
        {
          type: "mc",
          q: "Luis: 'The ___ toys are here.' (children)",
          options: ["children's", "childrens", "childrens'"],
          a: "children's",
          tip: "'Children' is irregular, so add 's."
        },
        
        {
          type: "input",
          q: "Luis: 'The car is my uncle's. It is my ___ car.'",
          a: "uncle's",
          alts: [],
          tip: "Uncle's = singular possessive.",
          successTip: "🎉 Album complete! You mastered possessives! Singular's, Plurals', Irregulars'. Perfect! ✅"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTERY CHALLENGE (A2-)
    // ===================================================================
    {
      id: "u4-set10",
      title: "Set 10: Family Tree Complete",
      context: `*Proyecto Final - Árbol Genealógico*

Luis completa un árbol genealógico completo.
"My mother's brother is my uncle. His son is my cousin."
"I have brothers, sisters, cousins, aunts, and uncles!"

*Final Project - Family Tree*

Luis completes a full family tree.
"My mother's brother is my uncle. His son is my cousin."
"I have brothers, sisters, cousins, aunts, and uncles!"`,
      
      questions: [
        {
          type: "mc",
          q: "Luis: 'My mother's brother is my ___ and his son is my ___.'",
          options: ["uncle / cousin", "aunt / nephew", "father / brother"],
          a: "uncle / cousin",
          tip: "Mother's brother = uncle. His son = your cousin.",
          successTip: "Family tree logic! Mother's brother = uncle. Uncle's son = cousin. Chain logic! 🌳"
        },
        
        {
          type: "input",
          q: "Luis: 'The cat is Tom's. It is ___ cat.'",
          a: "Tom's",
          alts: ["tom's"],
          tip: "Tom's = possessive."
        },
        
        {
          type: "match",
          q: "Match advanced family vocabulary.",
          pairs: [
            { left: "Niece", right: "Brother's daughter" },
            { left: "Nephew", right: "Sister's son" },
            { left: "Cousin", right: "Aunt's child" }
          ],
          a: "completed",
          tip: "Extended family.",
          successTip: "Advanced family! Niece/Nephew = siblings' children. Cousin = aunt/uncle's children. Complex relationships! 👨‍👩‍👧‍👦"
        },
        
        {
          type: "mc",
          q: "Luis points far (plural): '___ birds are over there!'",
          options: ["Those", "These", "This"],
          a: "Those",
          tip: "THOSE = plural + far."
        },
        
        {
          type: "input",
          q: "Luis: 'My sister has a husband. He is my ___.'",
          a: "brother-in-law",
          alts: ["brother in law", "brotherinlaw"],
          tip: "Sister's husband = brother-in-law.",
          successTip: "In-laws! Sister's husband = brother-in-law. Spouse's family = in-laws. Advanced vocab! 👨"
        },
        
        {
          type: "order",
          q: "Traduce: 'Mis abuelos viven en una granja.'",
          words: ["My", "grandparents", "live", "on", "a", "farm."],
          a: "My grandparents live on a farm.",
          extra: ["in", "at"],
          tip: "'On a farm' = en una granja."
        },
        
        {
          type: "mc",
          q: "Luis: 'My cousin's father is my ___.'",
          options: ["uncle", "grandfather", "brother"],
          a: "uncle",
          tip: "Cousin's father = your uncle.",
          successTip: "Logic puzzle solved! Cousin's father = YOUR uncle (because your uncle's child = your cousin). Perfect! 🧠✅"
        },
        
        {
          type: "input",
          q: "Luis: 'Opposite of nephew is ___.'",
          a: "niece",
          alts: [],
          tip: "Nephew (male) → Niece (female)."
        },
        
        {
          type: "order",
          q: "Traduce: 'Me encanta mi familia.'",
          words: ["I", "love", "my", "family."],
          a: "I love my family.",
          extra: ["like", "have"],
          tip: "'Love' = strong feeling (amar)."
        },
        
        {
          type: "mc",
          q: "Luis: 'The ___ toys are in the yard.' (dogs - plural)",
          options: ["dogs'", "dog's", "dogs"],
          a: "dogs'",
          tip: "Dogs' = plural possessive (apostrophe after s).",
          successTip: "🎉 UNIT 4 COMPLETE! You mastered family vocab, possessives ('s), and this/that/these/those! Ready for Unit 5! 🚀"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (A2)
  // ===================================================================
  readingSet: {
    id: "u4-reading",
    title: "Set 11: Reading Practice",
    category: "reading",
    
    questions: [
      {
        type: "passage",
        passage: `**Luis's Family**

Hi! My name is Luis. I have two brothers and one sister. My father's name is Carlos. My mother's name is Elena. We live in Australia.`
      },
      
      {
        type: "mc",
        q: "How many brothers does Luis have?",
        options: ["Two", "One", "Three"],
        a: "Two",
        tip: "Look for 'I have two brothers'."
      },
      
      {
        type: "mc",
        q: "What is Luis's father's name?",
        options: ["Carlos", "Luis", "Elena"],
        a: "Carlos",
        tip: "Father's name = Carlos."
      },
      
      {
        type: "mc",
        q: "Where does Luis live?",
        options: ["Australia", "Spain", "Mexico"],
        a: "Australia",
        tip: "They live in Australia.",
        successTip: "Reading carefully! 'We live in Australia' = exact info. ✅"
      },
      
      {
        type: "passage",
        passage: `**Luis's Pets**

Luis has two pets. He has a dog and a cat. The dog's name is Max. Max is brown. The cat's name is Luna. Luna is white.`
      },
      
      {
        type: "mc",
        q: "What pets does Luis have?",
        options: ["A dog and a cat", "Two dogs", "Two cats"],
        a: "A dog and a cat",
        tip: "He has a dog and a cat."
      },
      
      {
        type: "mc",
        q: "What is the dog's name?",
        options: ["Max", "Luna", "Luis"],
        a: "Max",
        tip: "Dog's name = Max."
      },
      
      {
        type: "mc",
        q: "What color is Luna?",
        options: ["White", "Brown", "Black"],
        a: "White",
        tip: "Luna is white.",
        successTip: "Colors + pets! Reading comprehension perfect! 🐱"
      },
      
      {
        type: "passage",
        passage: `**Luis's Extended Family**

Luis has an uncle. His uncle's name is Tom. Tom lives on a farm. The farm has three cows and two horses. Luis visits his uncle on weekends.`
      },
      
      {
        type: "mc",
        q: "Who is Tom?",
        options: ["Luis's uncle", "Luis's father", "Luis's brother"],
        a: "Luis's uncle",
        tip: "Tom = uncle."
      },
      
      {
        type: "mc",
        q: "Where does Tom live?",
        options: ["On a farm", "In a city", "In Australia"],
        a: "On a farm",
        tip: "Tom lives on a farm."
      },
      
      {
        type: "mc",
        q: "How many cows are on the farm?",
        options: ["Three", "Two", "Five"],
        a: "Three",
        tip: "Three cows and two horses.",
        successTip: "Perfect! You can read about families, pets, and places! Reading mastered! 🎉"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (A2)
  // ===================================================================
  reviewSet: {
    id: "u4-review",
    title: "Set 12: Unit Review",
    category: "review",
    
    questions: [
      {
        type: "match",
        q: "Review: Match family basics.",
        pairs: [
          { left: "Father", right: "Dad" },
          { left: "Mother", right: "Mom" },
          { left: "Brother", right: "Sister's opposite" }
        ],
        a: "completed",
        tip: "Casual vs formal family words."
      },
      
      {
        type: "mc",
        q: "Review: 'This is ___ car.' (John)",
        options: ["John's", "Johns", "John"],
        a: "John's",
        tip: "Possessive 's."
      },
      
      {
        type: "input",
        q: "Review: 'The cat is my sister's. It is my ___ cat.'",
        a: "sister's",
        alts: [],
        tip: "Singular possessive."
      },
      
      {
        type: "mc",
        q: "Review: 'My aunt's son is my ___.'",
        options: ["cousin", "brother", "nephew"],
        a: "cousin",
        tip: "Aunt's son = cousin."
      },
      
      {
        type: "order",
        q: "Review: Traduce: 'Ese es el perro de mi padre.'",
        words: ["That", "is", "my", "father's", "dog."],
        a: "That is my father's dog.",
        extra: ["fathers", "dog's"],
        tip: "Possessive structure."
      },
      
      {
        type: "mc",
        q: "Review: (Near) '___ is my dog.'",
        options: ["This", "That", "These"],
        a: "This",
        tip: "THIS = near + singular."
      },
      
      {
        type: "mc",
        q: "Review: (Far) '___ is my cat over there.'",
        options: ["That", "This", "Those"],
        a: "That",
        tip: "THAT = far + singular."
      },
      
      {
        type: "input",
        q: "Review: 'The house is my parents'. It is my ___ house.'",
        a: "parents'",
        alts: [],
        tip: "Plural possessive (apostrophe after s)."
      },
      
      {
        type: "match",
        q: "Review: Match animals to sounds.",
        pairs: [
          { left: "Dog", right: "Woof" },
          { left: "Cat", right: "Meow" },
          { left: "Cow", right: "Moo" }
        ],
        a: "completed",
        tip: "Animal sounds."
      },
      
      {
        type: "mc",
        q: "Review: 'I have a pet. It is a ___.'",
        options: ["dog", "table", "car"],
        a: "dog",
        tip: "Pets = animals at home.",
        successTip: "🎉 UNIT 4 REVIEW COMPLETE! You mastered family, pets, possessives, and demonstratives! Next: Unit 5! 🚀"
      }
    ]
  }
};
